import React, { useState, useEffect, useMemo } from 'react';
import { supabase, Mentor } from '../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  Plus, 
  Edit, 
  Edit2,
  Trash2, 
  X,
  Mail,
  Linkedin,
  Github,
  Instagram,
  Globe,
  DollarSign,
  User,
  Briefcase,
  Image,
  Settings,
  Play,
  Video,
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  MessageSquare,
  Share2,
  ExternalLink,
  Sparkles,
  Copy,
  Check
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUsers } from '../contexts/UsersContext';
import ImageUploader from './ImageUploader';
import { extractYouTubeVideoId, buildYouTubeThumbnailUrl } from '../helpers/videoHelpers';
import { MentorBioManagerModal } from './mentorship/MentorBioManagerModal';
import { MentorAdminModal } from './mentorship/MentorAdminModal';
import { mentorshipService, slugify, buildDefaultMentorAreaConfig } from '../services/mentorshipService';
import { MentorAreaConfig, SocialEntry, normalizeSocials, MentorSocialLinks } from '../types/mentorship';

interface MentoriasPageProps {
  onBack?: () => void;
}

const MentoriasPage: React.FC<MentoriasPageProps> = () => {
  const { user, isAdmin, isMentor } = useAuth();
  const { users } = useUsers();

  const canEditMentor = (mentor: Mentor | null | undefined) => {
    if (!mentor) return false;
    const ownerId = mentor.user_id || mentor.area_config?.mentorUserId;
    return Boolean(isAdmin || (isMentor && ownerId && user?.id === ownerId));
  };

  // Helper para extrair lista de entradas de redes sociais do mentor de forma retrocompatível
  const getMentorSocials = (mentor: Mentor | null | undefined, network: keyof MentorSocialLinks): SocialEntry[] => {
    if (!mentor) return [];
    const fromAreaBio = (mentor.area_config?.bioConfig?.socials as any)?.[network];
    const fromArea = (mentor.area_config?.socials as any)?.[network];
    const legacyCol = network === 'instagram' ? mentor.instagram_url :
                      network === 'whatsapp' ? mentor.whatsapp :
                      network === 'linkedin' ? mentor.linkedin_url :
                      network === 'github' ? mentor.github_url :
                      network === 'website' ? mentor.website_url : undefined;

    const rawList = fromAreaBio || fromArea || (legacyCol ? [{ url: legacyCol }] : []);
    const normalized = normalizeSocials({ [network]: rawList })[network] || [];
    return normalized;
  };

  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMentor, setEditingMentor] = useState<Mentor | null>(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mentorToDelete, setMentorToDelete] = useState<Mentor | null>(null);

  // States and functions for Free Content Management
  const [editingFreeContentMentorId, setEditingFreeContentMentorId] = useState<string | null>(null);
  const [editFreeContentForm, setEditFreeContentForm] = useState<{ title: string; videos: { title: string; url: string }[] }>({ title: '', videos: [] });
  const [savingFreeContent, setSavingFreeContent] = useState(false);
  const [playingVideo, setPlayingVideo] = useState<{ title: string; url: string } | null>(null);
  const [copiedEmailMentorId, setCopiedEmailMentorId] = useState<string | null>(null);

  const [selectedMentor, setSelectedMentor] = useState<Mentor | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isSliding, setIsSliding] = useState(false);
  const [visibleCards, setVisibleCards] = useState(3);
  const [isPaused, setIsPaused] = useState(false);

  // Auxiliares do carrossel
  const carouselMentors = mentors.length >= 3 ? [...mentors, ...mentors, ...mentors] : [];
  const M = mentors.length;
  const N = carouselMentors.length;

  useEffect(() => {
    if (M >= 3) {
      setCarouselIndex(M);
    }
  }, [M]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleCards(1);
      } else if (window.innerWidth < 1024) {
        setVisibleCards(2);
      } else {
        setVisibleCards(3);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isPaused || M < 3) return;
    
    const timer = setInterval(() => {
      if (isSliding) return;
      setIsSliding(true);
      setIsTransitioning(true);
      setCarouselIndex((prev) => prev + 1);
    }, 5000);

    return () => clearInterval(timer);
  }, [isPaused, isSliding, M]);

  const handleTransitionEnd = () => {
    setIsSliding(false);
    if (M < 3) return;
    
    if (carouselIndex >= 2 * M) {
      setIsTransitioning(false);
      setCarouselIndex(carouselIndex - M);
    }
    else if (carouselIndex < M) {
      setIsTransitioning(false);
      setCarouselIndex(carouselIndex + M);
    }
  };

  useEffect(() => {
    if (!isTransitioning) {
      const raf = requestAnimationFrame(() => {
        setIsTransitioning(true);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [isTransitioning]);

  const handlePrev = () => {
    if (isSliding || M < 3) return;
    setIsSliding(true);
    setIsTransitioning(true);
    setCarouselIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    if (isSliding || M < 3) return;
    setIsSliding(true);
    setIsTransitioning(true);
    setCarouselIndex((prev) => prev + 1);
  };

  const handleEditFreeContent = (mentor: Mentor) => {
    setEditingFreeContentMentorId(mentor.id);
    setEditFreeContentForm(mentor.free_content || { title: 'Conteúdos Gratuitos', videos: [] });
  };

  const handleSaveFreeContent = async (mentorId: string) => {
    try {
      setSavingFreeContent(true);
      const { error } = await supabase
        .from('mentors')
        .update({
          free_content: editFreeContentForm,
          updated_at: new Date().toISOString()
        })
        .eq('id', mentorId);

      if (error) {
        console.error('Erro ao salvar conteúdos gratuitos:', error);
        setError('Erro ao salvar conteúdos gratuitos');
        return;
      }

      await loadMentors();
      setEditingFreeContentMentorId(null);
    } catch (err) {
      console.error('Erro inesperado:', err);
      setError('Erro inesperado ao salvar conteúdos');
    } finally {
      setSavingFreeContent(false);
    }
  };

  const [scrollState, setScrollState] = useState<Record<string, { canScrollLeft: boolean; canScrollRight: boolean }>>({});

  const handleScroll = (mentorId: string) => {
    const container = document.getElementById(`scroll-${mentorId}`);
    if (container) {
      const canScrollLeft = container.scrollLeft > 10;
      const canScrollRight = container.scrollWidth - container.clientWidth - container.scrollLeft > 10;
      
      setScrollState(prev => ({
        ...prev,
        [mentorId]: { canScrollLeft, canScrollRight }
      }));
    }
  };

  useEffect(() => {
    if (mentors.length > 0) {
      const timer1 = setTimeout(() => {
        mentors.forEach(m => handleScroll(m.id));
      }, 50);

      const timer2 = setTimeout(() => {
        mentors.forEach(m => handleScroll(m.id));
      }, 300);

      const timer3 = setTimeout(() => {
        mentors.forEach(m => handleScroll(m.id));
      }, 800);
      
      const handleResize = () => {
        mentors.forEach(m => handleScroll(m.id));
      };
      
      window.addEventListener('resize', handleResize);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [mentors]);

  const scrollContainer = (id: string, direction: 'left' | 'right') => {
    const container = document.getElementById(id);
    if (container) {
      const scrollAmount = direction === 'left' ? -350 : 350;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      // Trigger scroll check after animation
      setTimeout(() => {
        const mentorId = id.replace('scroll-', '');
        handleScroll(mentorId);
      }, 350);
    }
  };

  // Header configuration state
  const [headerConfig, setHeaderConfig] = useState(() => {
    const saved = localStorage.getItem('masterclub_mentors_header');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.titlePart1 !== undefined) return parsed;
      } catch (e) {
        // use default
      }
    }
    return {
      titlePart1: 'Conheça os',
      titleHighlight: 'mentores',
      titlePart2: 'da nossa comunidade',
      description: 'Aprenda diretamente com quem já tem resultados reais e acelere seu aprendizado em e-commerce e marketing.'
    };
  });
  const [isHeaderModalOpen, setIsHeaderModalOpen] = useState(false);
  const [headerForm, setHeaderForm] = useState({ titlePart1: '', titleHighlight: '', titlePart2: '', description: '' });

  // States para Gestão de Bio Links & Editor
  const [isBioManagerOpen, setIsBioManagerOpen] = useState(false);
  const [isMentorAdminModalOpen, setIsMentorAdminModalOpen] = useState(false);
  const [adminModalConfig, setAdminModalConfig] = useState<MentorAreaConfig | null>(null);
  const [activeBioMentorTarget, setActiveBioMentorTarget] = useState<Mentor | null>(null);
  const [adminModalInitialTab, setAdminModalInitialTab] = useState<'geral' | 'hero' | 'modulos' | 'eventos' | 'suporte' | 'alunos' | 'bio'>('bio');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    slug: '',
    user_id: '' as string | null,
    bio: '',
    specialties: '',
    experience_years: 0,
    contact_email: '',
    linkedin_url: '',
    github_url: '',
    instagram_url: '',
    instagram_entries: [{ url: '', label: '' }] as SocialEntry[],
    website_url: '',
    checkout_url: '',
    whatsapp: '',
    hourly_rate: 0,
    session_types: '',
    languages: '',
    availability: '',
    banner_url: '',
    banner_vertical_url: '',
    avatar_url: ''
  });

  // Filtrar apenas Mentores e Admins para o vínculo de propriedade (facilitando a busca)
  const eligibleUsers = useMemo(() => {
    return users
      .filter((u) => u.role === 'mentor' || u.role === 'admin' || u.id === formData.user_id)
      .sort((a, b) => {
        // Mentores primeiro, depois Admins, depois alfabético
        if (a.role === 'mentor' && b.role !== 'mentor') return -1;
        if (a.role !== 'mentor' && b.role === 'mentor') return 1;
        if (a.role === 'admin' && b.role !== 'admin') return -1;
        if (a.role !== 'admin' && b.role === 'admin') return 1;
        const nameA = (a.full_name || a.email).toLowerCase();
        const nameB = (b.full_name || b.email).toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [users, formData.user_id]);

  const handleOpenConfigureBio = async (mentor: Mentor, tab: 'geral' | 'hero' | 'modulos' | 'eventos' | 'suporte' | 'alunos' | 'bio' = 'bio') => {
    setActiveBioMentorTarget(mentor);
    setAdminModalInitialTab(tab);
    
    // Buscar configuração atualizada ou gerar padrão
    const slugToFetch = mentor.slug || slugify(mentor.name);
    const cfg = await mentorshipService.fetchMentorshipConfig(slugToFetch);
    setAdminModalConfig(cfg);
    setIsMentorAdminModalOpen(true);
  };

  const handleSaveAdminConfig = async (newConfig: MentorAreaConfig) => {
    setAdminModalConfig(newConfig);
    const targetSlug = newConfig.slug || activeBioMentorTarget?.slug || slugify(newConfig.mentorName);
    await mentorshipService.saveMentorshipConfigToSupabase(targetSlug, newConfig);
    await loadMentors(true); // Recarregar em background de forma silenciosa, sem desmontar o modal
  };

  useEffect(() => {
    console.log('MentoriasPage montado, carregando mentores...');
    loadMentors();
  }, []);

  const loadMentors = async (silent = false) => {
    try {
      console.log('Iniciando loadMentors (silent =', silent, ')...');
      if (!silent) setLoading(true);
      const { data, error } = await supabase
        .from('mentors')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao carregar mentores:', error);
        if (!silent) setError('Erro ao carregar mentores: ' + error.message);
        return;
      }

      console.log('Raw data from database:', data);
      
      if (data && data.length > 0) {
        console.log('Primeiro mentor:', data[0]);
        console.log('checkout_url do primeiro mentor:', data[0].checkout_url);
      }

      setMentors((data as unknown as Mentor[]) || []);
      console.log('Mentores salvos no state:', data);
    } catch (err) {
      console.error('Erro inesperado:', err);
      if (!silent) setError('Erro inesperado ao carregar mentores');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'name' && !editingMentor && typeof value === 'string') {
        updated.slug = slugify(value);
      }
      if (field === 'slug' && typeof value === 'string') {
        updated.slug = slugify(value);
      }
      return updated;
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      slug: '',
      user_id: '' as string | null,
      bio: '',
      specialties: '',
      experience_years: 0,
      contact_email: '',
      linkedin_url: '',
      github_url: '',
      instagram_url: '',
      instagram_entries: [{ url: '', label: '' }],
      website_url: '',
      checkout_url: '',
      whatsapp: '',
      hourly_rate: 0,
      session_types: '',
      languages: '',
      availability: '',
      banner_url: '',
      banner_vertical_url: '',
      avatar_url: ''
    });
    setEditingMentor(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setSaving(true);
      
      const cleanSlug = slugify(formData.slug || formData.name);

      // Processar múltiplos links de Instagram
      const validInstagrams: SocialEntry[] = (formData.instagram_entries || [])
        .map((e) => ({
          url: typeof e.url === 'string' ? e.url.trim() : '',
          label: typeof e.label === 'string' && e.label.trim() ? e.label.trim() : undefined,
        }))
        .filter((e) => Boolean(e.url));

      const primaryInstagramUrl = validInstagrams.length > 0
        ? validInstagrams[0].url
        : (formData.instagram_url || '').trim();

      const { instagram_entries, ...restFormData } = formData;

      const mentorData = {
        ...restFormData,
        slug: cleanSlug,
        user_id: formData.user_id || null,
        name: formData.name.trim(),
        title: formData.title.trim(),
        bio: formData.bio.trim(),
        contact_email: formData.contact_email.trim(),
        linkedin_url: formData.linkedin_url.trim(),
        github_url: formData.github_url.trim(),
        instagram_url: primaryInstagramUrl,
        website_url: formData.website_url.trim(),
        checkout_url: formData.checkout_url.trim(),
        whatsapp: formData.whatsapp.trim(),
        availability: formData.availability.trim(),
        banner_url: formData.banner_url.trim(),
        banner_vertical_url: formData.banner_vertical_url.trim(),
        avatar_url: formData.avatar_url.trim(),
        specialties: formData.specialties.split(',').map((s) => s.trim()).filter((s) => s),
        session_types: formData.session_types.split(',').map((s) => s.trim()).filter((s) => s),
        languages: formData.languages.split(',').map((s) => s.trim()).filter((s) => s),
        created_by: user.id,
        is_active: true,
      };

      if (editingMentor) {
        // Update existing mentor and ensure area_config (especially bio avatar and name) is synced
        const existingBioSocials = editingMentor.area_config?.bioConfig?.socials || {};
        const existingRootSocials = editingMentor.area_config?.socials || {};

        const mergedBioSocials = {
          ...existingBioSocials,
          instagram: validInstagrams.length > 0 ? validInstagrams : (existingBioSocials.instagram || (primaryInstagramUrl ? [{ url: primaryInstagramUrl }] : [])),
          ...(mentorData.whatsapp ? { whatsapp: typeof existingBioSocials.whatsapp === 'object' && Array.isArray(existingBioSocials.whatsapp) ? existingBioSocials.whatsapp : [{ url: mentorData.whatsapp }] } : {}),
          website: mentorData.website_url || existingBioSocials.website || '',
        };

        const currentConfig = editingMentor.area_config
          ? {
              ...editingMentor.area_config,
              slug: cleanSlug,
              mentorUserId: formData.user_id || null,
              mentorName: mentorData.name,
              mentorTitle: mentorData.title,
              mentorAvatarUrl: mentorData.avatar_url || editingMentor.area_config.mentorAvatarUrl || '',
              socials: {
                ...existingRootSocials,
                instagram: mergedBioSocials.instagram,
              },
              bioConfig: {
                ...(editingMentor.area_config.bioConfig || {}),
                slug: cleanSlug,
                bioAvatarUrl: mentorData.avatar_url || editingMentor.area_config.bioConfig?.bioAvatarUrl || '',
                bioTitle: mentorData.name || editingMentor.area_config.bioConfig?.bioTitle || '',
                bioSubtitle: mentorData.title || editingMentor.area_config.bioConfig?.bioSubtitle || '',
                bioDescription: mentorData.bio || editingMentor.area_config.bioConfig?.bioDescription || '',
                socials: mergedBioSocials,
              },
            }
          : buildDefaultMentorAreaConfig(
              {
                name: mentorData.name,
                title: mentorData.title,
                user_id: formData.user_id || null,
                avatar_url: mentorData.avatar_url,
                whatsapp: mentorData.whatsapp,
                instagram_url: primaryInstagramUrl,
                website_url: mentorData.website_url,
                bio: mentorData.bio,
                banner_url: mentorData.banner_url,
                banner_vertical_url: mentorData.banner_vertical_url,
              },
              cleanSlug
            );

        let updateError: any = null;
        const { error } = await supabase
          .from('mentors')
          .update({
            ...mentorData,
            area_config: currentConfig,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingMentor.id);

        updateError = error;

        if (updateError && (updateError.code === 'PGRST204' || updateError.message?.includes('user_id'))) {
          console.warn('⚠️ Coluna user_id não encontrada na tabela mentors, atualizando via area_config...');
          const { user_id, ...mentorDataWithoutUserId } = mentorData;
          const retry = await supabase
            .from('mentors')
            .update({
              ...mentorDataWithoutUserId,
              area_config: currentConfig,
              updated_at: new Date().toISOString(),
            })
            .eq('id', editingMentor.id);
          updateError = retry.error;
        }

        if (updateError) {
          console.error('Erro ao atualizar mentor:', updateError);
          setError(`Erro ao atualizar mentor: ${updateError.message || 'Erro no banco de dados'}`);
          return;
        }
      } else {
        // Create new mentor with default area_config
        const initialAreaConfig = buildDefaultMentorAreaConfig(
          {
            name: mentorData.name,
            title: mentorData.title,
            user_id: formData.user_id || null,
            avatar_url: mentorData.avatar_url,
            whatsapp: mentorData.whatsapp,
            instagram_url: primaryInstagramUrl,
            website_url: mentorData.website_url,
            bio: mentorData.bio,
            banner_url: mentorData.banner_url,
            banner_vertical_url: mentorData.banner_vertical_url,
          },
          cleanSlug
        );

        if (validInstagrams.length > 0 && initialAreaConfig.bioConfig) {
          initialAreaConfig.bioConfig.socials = {
            ...(initialAreaConfig.bioConfig.socials || {}),
            instagram: validInstagrams,
          };
          initialAreaConfig.socials = {
            ...(initialAreaConfig.socials || {}),
            instagram: validInstagrams,
          };
        }

        let insertError: any = null;
        const { error } = await supabase
          .from('mentors')
          .insert([{ ...mentorData, area_config: initialAreaConfig }]);

        insertError = error;

        if (insertError && (insertError.code === 'PGRST204' || insertError.message?.includes('user_id'))) {
          console.warn('⚠️ Coluna user_id não encontrada na tabela mentors, salvando via area_config...');
          const { user_id, ...mentorDataWithoutUserId } = mentorData;
          const retry = await supabase
            .from('mentors')
            .insert([{ ...mentorDataWithoutUserId, area_config: initialAreaConfig }]);
          insertError = retry.error;
        }

        if (insertError) {
          console.error('Erro ao criar mentor:', insertError);
          setError(`Erro ao criar mentor: ${insertError.message || 'Erro no banco de dados'}`);
          return;
        }
      }

      await loadMentors();
      resetForm();
    } catch (err: any) {
      console.error('Erro inesperado:', err);
      setError(`Erro inesperado: ${err?.message || ''}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (mentor: any) => {
    console.log('=== HANDLEEDIT CHAMADO ===');
    console.log('Mentor completo:', mentor);
    console.log('Mentor.checkout_url:', mentor.checkout_url);
    
    setEditingMentor(mentor);

    const existingInstagrams = getMentorSocials(mentor, 'instagram');
    const initialInstagramEntries: SocialEntry[] = existingInstagrams.length > 0
      ? existingInstagrams
      : mentor.instagram_url
        ? [{ url: mentor.instagram_url, label: '' }]
        : [{ url: '', label: '' }];
    
    const newFormData = {
      name: mentor.name || '',
      title: mentor.title || '',
      slug: mentor.slug || slugify(mentor.name) || '',
      user_id: mentor.user_id || mentor.area_config?.mentorUserId || '',
      bio: mentor.bio || '',
      specialties: Array.isArray(mentor.specialties) ? mentor.specialties.join(', ') : '',
      experience_years: mentor.experience_years || 0,
      contact_email: mentor.contact_email || '',
      linkedin_url: mentor.linkedin_url || '',
      github_url: mentor.github_url || '',
      instagram_url: mentor.instagram_url || (initialInstagramEntries[0]?.url || ''),
      instagram_entries: initialInstagramEntries,
      website_url: mentor.website_url || '',
      checkout_url: mentor.checkout_url || '',
      whatsapp: mentor.whatsapp || '',
      hourly_rate: mentor.hourly_rate || 0,
      session_types: Array.isArray(mentor.session_types) ? mentor.session_types.join(', ') : '',
      languages: Array.isArray(mentor.languages) ? mentor.languages.join(', ') : '',
      availability: mentor.availability || '',
      banner_url: mentor.banner_url || '',
      banner_vertical_url: mentor.banner_vertical_url || '',
      avatar_url: mentor.avatar_url || ''
    };
    
    console.log('newFormData completo:', newFormData);
    setFormData(newFormData);
    
    // Debug: verificar se o estado foi atualizado após um pequeno delay
    setTimeout(() => {
      console.log('FormData após setTimeout:', formData);
    }, 100);
    
    console.log('=== FIM HANDLEEDIT ===');
    setShowAddForm(true);
  };

  const handleDelete = async (mentorId: string) => {
    const mentor = mentors.find(m => m.id === mentorId);
    if (!mentor) return;
    
    setMentorToDelete(mentor);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!mentorToDelete) return;

    try {
      const { error } = await supabase
        .from('mentors')
        .delete()
        .eq('id', mentorToDelete.id);

      if (error) {
        console.error('Erro ao excluir mentor:', error);
        setError('Erro ao excluir mentor');
        return;
      }

      await loadMentors();
      setShowDeleteConfirm(false);
      setMentorToDelete(null);
    } catch (err) {
      console.error('Erro inesperado:', err);
      setError('Erro inesperado');
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setMentorToDelete(null);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121316] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0FFF55] mx-auto mb-4"></div>
          <p className="text-white">Carregando mentorias...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121316]">
      {/* Header */}
      <div className="container mx-auto px-4 pt-8 pb-24 mt-16">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-white">
                {headerConfig.titlePart1}{' '}
                {headerConfig.titleHighlight && (
                  <span className="text-[#0FFF55]">{headerConfig.titleHighlight}</span>
                )}{' '}
                {headerConfig.titlePart2}
              </h1>
              {isAdmin && (
                <button
                  onClick={() => {
                    setHeaderForm({
                      titlePart1: headerConfig.titlePart1 || '',
                      titleHighlight: headerConfig.titleHighlight || '',
                      titlePart2: headerConfig.titlePart2 || '',
                      description: headerConfig.description
                    });
                    setIsHeaderModalOpen(true);
                  }}
                  className="p-1.5 text-gray-500 hover:text-white rounded hover:bg-gray-800 transition-all"
                  title="Editar cabeçalho"
                >
                  <Settings className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl leading-relaxed">
              {headerConfig.description}
            </p>
          </div>
          
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2.5 self-start sm:self-center">
              <Button 
                onClick={() => setIsBioManagerOpen(true)}
                variant="outline"
                className="border-[#0FFF55]/40 hover:border-[#0FFF55] text-white hover:text-[#0FFF55] bg-black/40 hover:bg-[#0FFF55]/10 font-bold shadow-lg shadow-[#0FFF55]/5"
              >
                <Share2 className="w-4 h-4 mr-2 text-[#0FFF55]" />
                Gestão de Bio Links & Slugs
              </Button>
              
              <Button 
                onClick={() => setShowAddForm(true)}
                className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold shadow-lg shadow-[#0FFF55]/15"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Mentor
              </Button>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <Card className="mb-8 bg-[#1a1d23]/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                {editingMentor ? 'Editar Mentor' : 'Adicionar Novo Mentor'}
                <Button
                  onClick={resetForm}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-white">Nome Completo</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                      placeholder="Ex: Vitor Terra"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="title" className="text-white">Título/Especialidade</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                      placeholder="Ex: Engenheiro de Software"
                    />
                  </div>

                  <div className="md:col-span-2 bg-[#121316]/60 border border-white/10 rounded-xl p-3.5 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="slug" className="text-white font-bold text-xs flex items-center gap-1.5">
                        <Share2 className="w-3.5 h-3.5 text-[#0FFF55]" /> Slug da Página (URL única na web)
                      </Label>
                      <span className="text-[10px] text-gray-400 font-mono">Gerada automaticamente a partir do nome</span>
                    </div>
                    <div className="flex items-center">
                      <span className="px-3 py-2 bg-gray-800/80 border border-r-0 border-gray-700 rounded-l-md text-xs text-gray-400 font-mono">
                        /bio-
                      </span>
                      <Input
                        id="slug"
                        value={formData.slug}
                        onChange={(e) => handleInputChange('slug', e.target.value)}
                        className="bg-gray-700/30 border-gray-700 text-white font-mono rounded-l-none text-xs"
                        placeholder="nome-do-mentor"
                      />
                    </div>
                    <div className="flex flex-wrap gap-3 text-[11px] text-gray-400 font-mono pt-1 items-center">
                      <a
                        href={`/bio-${formData.slug || 'slug'}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 hover:text-[#0FFF55] transition-colors group"
                        title="Abrir página Bio"
                      >
                        🌐 Link da Bio:{' '}
                        <strong className="text-white group-hover:text-[#0FFF55] transition-colors">
                          masterclubbr.vip/bio-{formData.slug || 'slug'}
                        </strong>
                        <ExternalLink className="w-3 h-3 text-[#0FFF55] opacity-70 group-hover:opacity-100" />
                      </a>
                      <a
                        href={`/mentoria-${formData.slug || 'slug'}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 hover:text-[#0FFF55] transition-colors group"
                        title="Abrir Área de Mentoria"
                      >
                        🎓 Mentoria:{' '}
                        <strong className="text-gray-300 group-hover:text-[#0FFF55] transition-colors">
                          masterclubbr.vip/mentoria-{formData.slug || 'slug'}
                        </strong>
                        <ExternalLink className="w-3 h-3 text-[#0FFF55] opacity-70 group-hover:opacity-100" />
                      </a>
                    </div>
                  </div>

                  {isAdmin && (
                    <div className="md:col-span-2 bg-[#121316]/80 border border-[#0FFF55]/30 rounded-xl p-3.5 space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="user_id" className="text-white font-bold text-xs flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5 text-[#0FFF55]" /> Usuário Proprietário (Vínculo de Login & Dashboard)
                        </Label>
                        <span className="text-[10px] text-[#0FFF55] font-medium">Exibindo apenas Mentores e Admins</span>
                      </div>
                      <select
                        id="user_id"
                        value={formData.user_id || ''}
                        onChange={(e) => handleInputChange('user_id', e.target.value || null)}
                        className="w-full px-3.5 py-2.5 bg-gray-800/90 border border-gray-700 focus:border-[#0FFF55] rounded-xl text-xs text-white focus:outline-none"
                      >
                        <option value="">Nenhum usuário vinculado (Acesso exclusivo de administradores)</option>
                        {eligibleUsers.filter((u) => u.role === 'mentor').length > 0 && (
                          <optgroup label="★ Mentores Promovidos">
                            {eligibleUsers
                              .filter((u) => u.role === 'mentor')
                              .map((u) => (
                                <option key={u.id} value={u.id}>
                                  ★ {u.full_name ? `${u.full_name} (${u.email})` : u.email}
                                </option>
                              ))}
                          </optgroup>
                        )}
                        {eligibleUsers.filter((u) => u.role === 'admin').length > 0 && (
                          <optgroup label="🛡️ Administradores">
                            {eligibleUsers
                              .filter((u) => u.role === 'admin')
                              .map((u) => (
                                <option key={u.id} value={u.id}>
                                  🛡️ {u.full_name ? `${u.full_name} (${u.email})` : u.email}
                                </option>
                              ))}
                          </optgroup>
                        )}
                        {eligibleUsers.filter((u) => u.role !== 'mentor' && u.role !== 'admin').length > 0 && (
                          <optgroup label="Outros vinculados anteriormente">
                            {eligibleUsers
                              .filter((u) => u.role !== 'mentor' && u.role !== 'admin')
                              .map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.full_name ? `${u.full_name} (${u.email})` : u.email} — [{u.role}]
                                </option>
                              ))}
                          </optgroup>
                        )}
                      </select>
                      <p className="text-[11px] text-gray-400">
                        O usuário selecionado poderá editar este mentor e gerenciar links de bio e alunos vinculados.
                      </p>
                    </div>
                  )}
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="bio" className="text-white">Biografia (muito breve)</Label>
                    <Textarea
                      id="bio"
                      value={formData.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      className="bg-gray-700/30 border-gray-600/50 text-white min-h-[60px]"
                      placeholder="Uma descrição muito breve do mentor..."
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="specialties" className="text-white">Especialidades (separadas por vírgula)</Label>
                    <Input
                      id="specialties"
                      value={formData.specialties}
                      onChange={(e) => handleInputChange('specialties', e.target.value)}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                      placeholder="React, TypeScript, Node.js"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="experience_years" className="text-white">Anos de Experiência</Label>
                    <Input
                      id="experience_years"
                      type="number"
                      value={formData.experience_years || ''}
                      onChange={(e) => handleInputChange('experience_years', e.target.value ? parseInt(e.target.value) || 0 : '')}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="contact_email" className="text-white">Email de Contato</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => handleInputChange('contact_email', e.target.value)}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="whatsapp" className="text-white">WhatsApp (Link ou número)</Label>
                    <Input
                      id="whatsapp"
                      value={formData.whatsapp}
                      onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                      placeholder="Ex: https://wa.me/5511999999999"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="hourly_rate" className="text-white">Valor por Hora (R$) - Opcional</Label>
                    <Input
                      id="hourly_rate"
                      type="number"
                      value={formData.hourly_rate || ''}
                      onChange={(e) => handleInputChange('hourly_rate', e.target.value ? parseFloat(e.target.value) || 0 : '')}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                      placeholder="Deixe em branco para Sob Consulta"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="linkedin_url" className="text-white">LinkedIn</Label>
                    <Input
                      id="linkedin_url"
                      value={formData.linkedin_url}
                      onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="github_url" className="text-white">GitHub</Label>
                    <Input
                      id="github_url"
                      value={formData.github_url}
                      onChange={(e) => handleInputChange('github_url', e.target.value)}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                    />
                  </div>
                  
                  <div className="md:col-span-2 bg-[#121316]/90 border border-white/10 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Instagram className="w-4 h-4 text-[#E4405F]" />
                        <span className="text-xs font-bold text-white uppercase tracking-wider">
                          Instagram (Perfis & Sócios)
                        </span>
                        {formData.instagram_entries.length > 1 && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-[#E4405F]/15 text-[#E4405F] border border-[#E4405F]/30">
                            {formData.instagram_entries.length} perfis
                          </span>
                        )}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            instagram_entries: [...prev.instagram_entries, { url: '', label: '' }],
                          }));
                        }}
                        className="h-7 px-2.5 text-[11px] font-bold border-[#E4405F]/30 text-[#E4405F] hover:bg-[#E4405F]/15 hover:text-white transition-all"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" />
                        Adicionar outro Instagram
                      </Button>
                    </div>

                    <div className="space-y-2.5">
                      {formData.instagram_entries.map((entry, idx) => (
                        <div key={idx} className="flex items-start gap-2 bg-[#1a1b1f] p-2.5 rounded-xl border border-white/8">
                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px] text-gray-400 font-semibold block mb-1">
                                Link do Perfil {formData.instagram_entries.length > 1 ? `#${idx + 1}` : ''}
                              </Label>
                              <Input
                                value={entry.url}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setFormData((prev) => {
                                    const next = prev.instagram_entries.map((item, i) =>
                                      i === idx ? { ...item, url: val } : item
                                    );
                                    return {
                                      ...prev,
                                      instagram_entries: next,
                                      instagram_url: idx === 0 ? val : prev.instagram_url,
                                    };
                                  });
                                }}
                                placeholder="https://instagram.com/seuuser"
                                className="bg-gray-800/80 border-gray-700 text-white text-xs h-9"
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] text-gray-400 font-semibold block mb-1">
                                Rótulo / Nome de exibição (opcional)
                              </Label>
                              <Input
                                value={entry.label || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setFormData((prev) => {
                                    const next = prev.instagram_entries.map((item, i) =>
                                      i === idx ? { ...item, label: val } : item
                                    );
                                    return { ...prev, instagram_entries: next };
                                  });
                                }}
                                placeholder="ex: Santiago Suso"
                                className="bg-gray-800/80 border-gray-700 text-white text-xs h-9"
                              />
                            </div>
                          </div>
                          {formData.instagram_entries.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setFormData((prev) => {
                                  const next = prev.instagram_entries.filter((_, i) => i !== idx);
                                  return {
                                    ...prev,
                                    instagram_entries: next.length > 0 ? next : [{ url: '', label: '' }],
                                  };
                                });
                              }}
                              className="mt-5 p-2 h-9 w-9 text-red-400/80 hover:text-red-400 hover:bg-red-400/10 transition-all flex-shrink-0"
                              title="Remover este Instagram"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="website_url" className="text-white">Website</Label>
                    <Input
                      id="website_url"
                      value={formData.website_url}
                      onChange={(e) => handleInputChange('website_url', e.target.value)}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="checkout_url" className="text-white">URL de Checkout</Label>
                    <Input
                      id="checkout_url"
                      value={formData.checkout_url}
                      onChange={(e) => {
                        console.log('Mudando checkout_url para:', e.target.value);
                        handleInputChange('checkout_url', e.target.value);
                      }}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                      placeholder="https://checkout.stripe.com/seu-link-aqui"
                    />
                    <div className="text-xs text-gray-400 mt-1">
                      Valor atual: "{formData.checkout_url}" (length: {formData.checkout_url?.length || 0})
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="session_types" className="text-white">Tipos de Sessão (separadas por vírgula)</Label>
                    <Input
                      id="session_types"
                      value={formData.session_types}
                      onChange={(e) => handleInputChange('session_types', e.target.value)}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                      placeholder="1:1, Code Review, Arquitetura"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="languages" className="text-white">Idiomas (separados por vírgula)</Label>
                    <Input
                      id="languages"
                      value={formData.languages}
                      onChange={(e) => handleInputChange('languages', e.target.value)}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                      placeholder="Português, Inglês"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="availability" className="text-white">Disponibilidade</Label>
                    <Textarea
                      id="availability"
                      value={formData.availability}
                      onChange={(e) => handleInputChange('availability', e.target.value)}
                      className="bg-gray-700/30 border-gray-600/50 text-white"
                      placeholder="Segunda a Sexta, 19h às 22h"
                    />
                  </div>
                  
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <ImageUploader
                        bucketName="mentorias"
                        label="Foto/Logo do Mentor"
                        value={formData.avatar_url}
                        onChange={(url) => handleInputChange('avatar_url', url)}
                        options={{ maxWidth: 400, maxHeight: 400, quality: 0.85, format: 'webp' }}
                        placeholder="Arraste a foto ou cole a URL"
                      />
                      <span className="text-[10px] text-gray-400 mt-1 block">
                        * Proporção ideal: <strong className="text-[#0FFF55]">400x400 pixels (1:1)</strong>.
                      </span>
                    </div>
                    <div>
                      <ImageUploader
                        bucketName="mentorias"
                        label="Banner Vertical (Card da Vitrine)"
                        value={formData.banner_vertical_url}
                        onChange={(url) => handleInputChange('banner_vertical_url', url)}
                        options={{ maxWidth: 400, maxHeight: 600, quality: 0.85, format: 'webp' }}
                        placeholder="Arraste o banner ou cole a URL"
                      />
                      <span className="text-[10px] text-gray-400 mt-1 block">
                        * Proporção ideal: <strong className="text-[#0FFF55]">400x600 pixels (2:3)</strong>.
                      </span>
                    </div>
                    <div>
                      <ImageUploader
                        bucketName="mentorias"
                        label="Banner Horizontal (Janela de Detalhes)"
                        value={formData.banner_url}
                        onChange={(url) => handleInputChange('banner_url', url)}
                        options={{ maxWidth: 1200, maxHeight: 400, quality: 0.85, format: 'webp' }}
                        placeholder="Arraste o banner ou cole a URL"
                      />
                      <span className="text-[10px] text-gray-400 mt-1 block">
                        * Proporção ideal: <strong className="text-[#0FFF55]">1200x400 pixels (3:1)</strong>.
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-4">
                  <Button
                    type="button"
                    onClick={resetForm}
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:text-white"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold"
                  >
                    {saving ? 'Salvando...' : (editingMentor ? 'Atualizar' : 'Criar')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && mentorToDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-[#1a1d23] border border-gray-700 rounded-xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Confirmar Exclusão</h3>
                  <p className="text-gray-400 text-sm">Esta ação não pode ser desfeita</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-300 mb-2">
                  Tem certeza que deseja excluir o mentor:
                </p>
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                  <p className="text-white font-medium">{mentorToDelete.name}</p>
                  <p className="text-[#0FFF55] text-sm">{mentorToDelete.title}</p>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  onClick={cancelDelete}
                  variant="outline"
                  className="flex-1 border-gray-600 text-gray-300 hover:text-white"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={confirmDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Mentor Carousel */}
        {mentors.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Nenhum mentor encontrado</h3>
            <p className="text-gray-400">Em breve teremos mentores disponíveis para você!</p>
          </div>
        ) : mentors.length < 3 ? (
          /* Static Centered List for 1 or 2 mentors */
          <div className="flex flex-wrap justify-center gap-6 max-w-[95%] xl:max-w-[1400px] mx-auto px-4 py-6">
            {mentors.map((mentor) => (
              <div 
                key={mentor.id}
                className="w-full sm:w-[380px] flex-shrink-0"
              >
                <Card className="bg-[#141517]/65 backdrop-blur-xl border border-white/5 hover:border-[#0FFF55]/30 shadow-2xl rounded-3xl overflow-hidden flex flex-col min-h-[560px] justify-between transition-all duration-300 hover:scale-[1.01]">
                  
                  {/* Header: Banner & Avatar */}
                  <div className="relative h-44 bg-gradient-to-r from-gray-900 to-gray-800">
                    {canEditMentor(mentor) && (
                      <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(mentor);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="px-2.5 py-1 bg-black/80 hover:bg-black text-[#0FFF55] hover:text-white rounded-lg border border-[#0FFF55]/40 hover:border-[#0FFF55] transition-all shadow-lg flex items-center space-x-1 text-xs font-semibold backdrop-blur-md"
                          title="Editar dados e fotos do card"
                        >
                          <Edit2 size={12} />
                          <span>Editar Card</span>
                        </button>
                        {isAdmin && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(mentor.id);
                            }}
                            className="p-1.5 bg-black/80 hover:bg-red-500/20 text-gray-400 hover:text-red-400 rounded-lg border border-white/10 hover:border-red-500/30 transition-all shadow-lg"
                            title="Excluir mentor"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    )}
                    {mentor.banner_vertical_url ? (
                      <img 
                        src={mentor.banner_vertical_url} 
                        alt="Banner" 
                        className="w-full h-full object-cover"
                      />
                    ) : mentor.banner_url ? (
                      <img 
                        src={mentor.banner_url} 
                        alt="Banner" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-r from-[#0FFF55]/5 to-[#0dd149]/5" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#141517] to-transparent" />
                    
                    {/* Avatar */}
                    <div className="absolute -bottom-10 left-6 w-20 h-20 rounded-full overflow-hidden border-2 border-[#0FFF55] bg-[#121316] shadow-xl flex items-center justify-center">
                      {mentor.avatar_url ? (
                        <img 
                          src={mentor.avatar_url} 
                          alt={mentor.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-8 h-8 text-gray-500" />
                      )}
                    </div>
                  </div>

                  {/* Body Info */}
                  <div className="px-6 pt-12 pb-4 flex-1 flex flex-col justify-between">
                    <div className="space-y-3">
                      {/* Name and Title */}
                      <div>
                        <h3 className="text-xl font-bold text-white tracking-wide">{mentor.name}</h3>
                        <p className="text-xs text-[#0FFF55] font-semibold tracking-wider uppercase mt-0.5">{mentor.title}</p>
                      </div>

                      {/* Experience and Hourly rate */}
                      <div className="flex flex-wrap gap-2 pt-1">
                        {mentor.experience_years && mentor.experience_years > 0 ? (
                          <div className="flex items-center space-x-1.5 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full text-[11px] font-medium text-gray-200">
                            <Briefcase size={12} className="text-[#0FFF55]" />
                            <span>{mentor.experience_years} anos exp.</span>
                          </div>
                        ) : null}
                        {mentor.hourly_rate && parseFloat(mentor.hourly_rate.toString()) > 0 ? (
                          <div className="flex items-center space-x-1.5 bg-[#0FFF55]/10 border border-[#0FFF55]/20 px-2.5 py-1 rounded-full text-[11px] font-semibold text-[#0FFF55]">
                            <DollarSign size={12} />
                            <span>{formatCurrency(parseFloat(mentor.hourly_rate.toString()))}/h</span>
                          </div>
                        ) : null}
                      </div>

                      {/* Brief Bio */}
                      <p className="text-gray-400 text-xs leading-relaxed line-clamp-3 pt-1">
                        {mentor.bio}
                      </p>
                    </div>

                    {/* Specialties badges */}
                    {mentor.specialties && mentor.specialties.filter(Boolean).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-3 mt-auto">
                        {mentor.specialties.filter(Boolean).slice(0, 3).map((spec, i) => (
                          <Badge key={i} className="bg-[#1a1d21] text-gray-300 border-gray-800 text-[10px] px-2 py-0.5">
                            {spec}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer Actions */}
                  <div className="p-6 pt-0 border-t border-white/5 mt-2 bg-[#121316]/40 space-y-2">
                    <button 
                      onClick={() => setSelectedMentor(mentor)}
                      className="w-full bg-[#0FFF55] hover:bg-[#0dd149] text-black text-xs font-bold py-3 px-4 rounded-xl flex items-center justify-center space-x-2 transition-all uppercase tracking-wider shadow-lg shadow-[#0FFF55]/10 hover:shadow-[#0FFF55]/20"
                    >
                      <span>Ir para a Mentoria</span>
                    </button>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const s = mentor.slug || slugify(mentor.name);
                          window.open(`/bio-${s}`, '_blank');
                        }}
                        className="w-full bg-white/5 hover:bg-white/10 text-gray-200 hover:text-white text-[11px] font-semibold py-2 px-2 rounded-lg flex items-center justify-center space-x-1.5 transition-all border border-white/5"
                        title="Ver página pública de links do mentor"
                      >
                        <Share2 size={12} className="text-[#0FFF55]" />
                        <span>Link da Bio</span>
                      </button>

                      {canEditMentor(mentor) ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenConfigureBio(mentor, 'bio');
                          }}
                          className="w-full bg-[#0FFF55]/10 hover:bg-[#0FFF55]/20 text-[#0FFF55] text-[11px] font-bold py-2 px-2 rounded-lg flex items-center justify-center space-x-1.5 transition-all border border-[#0FFF55]/20"
                          title="Configurar Bio Links deste mentor"
                        >
                          <Settings size={12} />
                          <span>Configurar Bio</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => setSelectedMentor(mentor)}
                          className="w-full bg-white/5 hover:bg-white/10 text-gray-300 text-[11px] font-medium py-2 px-2 rounded-lg flex items-center justify-center space-x-1.5 transition-all"
                        >
                          <User size={12} />
                          <span>Detalhes</span>
                        </button>
                      )}
                    </div>

                    {canEditMentor(mentor) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(mentor);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="w-full bg-[#1b1c22] hover:bg-[#23252c] text-gray-200 hover:text-white text-[11px] font-semibold py-2 px-2 rounded-lg flex items-center justify-center space-x-1.5 transition-all border border-white/10 hover:border-[#0FFF55]/30"
                        title="Editar fotos, biografia e informações do card"
                      >
                        <Edit2 size={12} className="text-[#0FFF55]" />
                        <span>Editar Informações do Card</span>
                      </button>
                    )}
                    
                    <div className="flex flex-wrap items-center justify-center gap-3 mt-3 pt-1">
                      {mentor.contact_email && mentor.contact_email.trim() && (
                        <a 
                          href={`mailto:${mentor.contact_email}`}
                          className="text-gray-400 hover:text-white transition-colors flex items-center space-x-1 text-xs"
                        >
                          <Mail size={12} />
                          <span>E-mail</span>
                        </a>
                      )}
                      {(() => {
                        const igs = getMentorSocials(mentor, 'instagram');
                        return igs.map((entry, idx) => (
                          <a 
                            key={`carousel-ig-${idx}`}
                            href={entry.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={entry.label ? `${entry.label} (${entry.url})` : entry.url}
                            className="text-gray-400 hover:text-[#E4405F] transition-colors flex items-center space-x-1 text-xs font-medium"
                          >
                            <Instagram size={12} className="text-[#E4405F]" />
                            <span>{entry.label || (igs.length > 1 ? `Instagram ${idx + 1}` : 'Instagram')}</span>
                          </a>
                        ));
                      })()}
                      {mentor.whatsapp && mentor.whatsapp.trim() && (
                        <a 
                          href={mentor.whatsapp.startsWith('http') ? mentor.whatsapp : `https://wa.me/${mentor.whatsapp.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-green-400 transition-colors flex items-center space-x-1 text-xs"
                        >
                          <MessageSquare size={12} className="text-green-400" />
                          <span>WhatsApp</span>
                        </a>
                      )}
                    </div>
                  </div>

                </Card>
              </div>
            ))}
          </div>
        ) : (
          <div 
            className="relative w-full max-w-[95%] xl:max-w-[1400px] mx-auto px-4 sm:px-12"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Viewport de corte */}
            <div className="overflow-hidden rounded-3xl">
              <div 
                className="flex transition-transform duration-700 ease-in-out"
                style={{
                  transform: `translateX(-${carouselIndex * (100 / visibleCards)}%)`,
                  transition: isTransitioning ? 'transform 700ms ease-in-out' : 'none'
                }}
                onTransitionEnd={handleTransitionEnd}
              >
                {carouselMentors.map((mentor, idx) => (
                  <div 
                    key={`${mentor.id}-${idx}`}
                    className="w-full md:w-1/2 lg:w-1/3 flex-shrink-0 p-3"
                  >
                    <Card className="bg-[#141517]/65 backdrop-blur-xl border border-white/5 hover:border-[#0FFF55]/30 shadow-2xl rounded-3xl overflow-hidden flex flex-col min-h-[560px] justify-between transition-all duration-300 hover:scale-[1.01]">
                      
                      {/* Header: Banner & Avatar */}
                      <div className="relative h-44 bg-gradient-to-r from-gray-900 to-gray-800">
                        {canEditMentor(mentor) && (
                          <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(mentor);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                              className="px-2.5 py-1 bg-black/80 hover:bg-black text-[#0FFF55] hover:text-white rounded-lg border border-[#0FFF55]/40 hover:border-[#0FFF55] transition-all shadow-lg flex items-center space-x-1 text-xs font-semibold backdrop-blur-md"
                              title="Editar dados e fotos do card"
                            >
                              <Edit2 size={12} />
                              <span>Editar Card</span>
                            </button>
                            {isAdmin && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(mentor.id);
                                }}
                                className="p-1.5 bg-black/80 hover:bg-red-500/20 text-gray-400 hover:text-red-400 rounded-lg border border-white/10 hover:border-red-500/30 transition-all shadow-lg"
                                title="Excluir mentor"
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        )}
                        {mentor.banner_vertical_url ? (
                          <img 
                            src={mentor.banner_vertical_url} 
                            alt="Banner" 
                            className="w-full h-full object-cover"
                          />
                        ) : mentor.banner_url ? (
                          <img 
                            src={mentor.banner_url} 
                            alt="Banner" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-r from-[#0FFF55]/5 to-[#0dd149]/5" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#141517] to-transparent" />
                        
                        {/* Avatar */}
                        <div className="absolute -bottom-10 left-6 w-20 h-20 rounded-full overflow-hidden border-2 border-[#0FFF55] bg-[#121316] shadow-xl flex items-center justify-center">
                          {mentor.avatar_url ? (
                            <img 
                              src={mentor.avatar_url} 
                              alt={mentor.name} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="w-8 h-8 text-gray-500" />
                          )}
                        </div>
                      </div>

                      {/* Body Info */}
                      <div className="px-6 pt-12 pb-4 flex-1 flex flex-col justify-between">
                        <div className="space-y-3">
                          {/* Name and Title */}
                          <div>
                            <h3 className="text-xl font-bold text-white tracking-wide">{mentor.name}</h3>
                            <p className="text-xs text-[#0FFF55] font-semibold tracking-wider uppercase mt-0.5">{mentor.title}</p>
                          </div>

                          {/* Experience and Hourly rate */}
                          <div className="flex flex-wrap gap-2 pt-1">
                            {mentor.experience_years && mentor.experience_years > 0 ? (
                              <div className="flex items-center space-x-1.5 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full text-[11px] font-medium text-gray-200">
                                <Briefcase size={12} className="text-[#0FFF55]" />
                                <span>{mentor.experience_years} anos exp.</span>
                              </div>
                            ) : null}
                            {mentor.hourly_rate && parseFloat(mentor.hourly_rate.toString()) > 0 ? (
                              <div className="flex items-center space-x-1.5 bg-[#0FFF55]/10 border border-[#0FFF55]/20 px-2.5 py-1 rounded-full text-[11px] font-semibold text-[#0FFF55]">
                                <DollarSign size={12} />
                                <span>{formatCurrency(parseFloat(mentor.hourly_rate.toString()))}/h</span>
                              </div>
                            ) : null}
                          </div>

                          {/* Brief Bio */}
                          <p className="text-gray-400 text-xs leading-relaxed line-clamp-3 pt-1">
                            {mentor.bio}
                          </p>
                        </div>

                        {/* Specialties badges */}
                        {mentor.specialties && mentor.specialties.filter(Boolean).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pt-3 mt-auto">
                            {mentor.specialties.filter(Boolean).slice(0, 3).map((spec, i) => (
                              <Badge key={i} className="bg-[#1a1d21] text-gray-300 border-gray-800 text-[10px] px-2 py-0.5">
                                {spec}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Footer Actions */}
                      <div className="p-6 pt-0 border-t border-white/5 mt-2 bg-[#121316]/40 space-y-2">
                        <button 
                          onClick={() => setSelectedMentor(mentor)}
                          className="w-full bg-[#0FFF55] hover:bg-[#0dd149] text-black text-xs font-bold py-3 px-4 rounded-xl flex items-center justify-center space-x-2 transition-all uppercase tracking-wider shadow-lg shadow-[#0FFF55]/10 hover:shadow-[#0FFF55]/20"
                        >
                          <span>Ir para a Mentoria</span>
                        </button>

                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const s = mentor.slug || slugify(mentor.name);
                              window.open(`/bio-${s}`, '_blank');
                            }}
                            className="w-full bg-white/5 hover:bg-white/10 text-gray-200 hover:text-white text-[11px] font-semibold py-2 px-2 rounded-lg flex items-center justify-center space-x-1.5 transition-all border border-white/5"
                            title="Ver página pública de links do mentor"
                          >
                            <Share2 size={12} className="text-[#0FFF55]" />
                            <span>Link da Bio</span>
                          </button>

                          {canEditMentor(mentor) ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenConfigureBio(mentor, 'bio');
                              }}
                              className="w-full bg-[#0FFF55]/10 hover:bg-[#0FFF55]/20 text-[#0FFF55] text-[11px] font-bold py-2 px-2 rounded-lg flex items-center justify-center space-x-1.5 transition-all border border-[#0FFF55]/20"
                              title="Configurar Bio Links deste mentor"
                            >
                              <Settings size={12} />
                              <span>Configurar Bio</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => setSelectedMentor(mentor)}
                              className="w-full bg-white/5 hover:bg-white/10 text-gray-300 text-[11px] font-medium py-2 px-2 rounded-lg flex items-center justify-center space-x-1.5 transition-all"
                            >
                              <User size={12} />
                              <span>Detalhes</span>
                            </button>
                          )}
                        </div>

                        {canEditMentor(mentor) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(mentor);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="w-full bg-[#1b1c22] hover:bg-[#23252c] text-gray-200 hover:text-white text-[11px] font-semibold py-2 px-2 rounded-lg flex items-center justify-center space-x-1.5 transition-all border border-white/10 hover:border-[#0FFF55]/30"
                            title="Editar fotos, biografia e informações do card"
                          >
                            <Edit2 size={12} className="text-[#0FFF55]" />
                            <span>Editar Informações do Card</span>
                          </button>
                        )}
                        
                        <div className="flex flex-wrap items-center justify-center gap-3 mt-3 pt-1">
                          {mentor.contact_email && mentor.contact_email.trim() && (
                            <a 
                              href={`mailto:${mentor.contact_email}`}
                              className="text-gray-400 hover:text-white transition-colors flex items-center space-x-1 text-xs"
                            >
                              <Mail size={12} />
                              <span>E-mail</span>
                            </a>
                          )}
                          {(() => {
                            const igs = getMentorSocials(mentor, 'instagram');
                            return igs.map((entry, idx) => (
                              <a 
                                key={`grid-ig-${idx}`}
                                href={entry.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={entry.label ? `${entry.label} (${entry.url})` : entry.url}
                                className="text-gray-400 hover:text-[#E4405F] transition-colors flex items-center space-x-1 text-xs font-medium"
                              >
                                <Instagram size={12} className="text-[#E4405F]" />
                                <span>{entry.label || (igs.length > 1 ? `Instagram ${idx + 1}` : 'Instagram')}</span>
                              </a>
                            ));
                          })()}
                          {mentor.whatsapp && mentor.whatsapp.trim() && (
                            <a 
                              href={mentor.whatsapp.startsWith('http') ? mentor.whatsapp : `https://wa.me/${mentor.whatsapp.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-400 hover:text-green-400 transition-colors flex items-center space-x-1 text-xs"
                            >
                              <MessageSquare size={12} className="text-green-400" />
                              <span>WhatsApp</span>
                            </a>
                          )}
                        </div>
                      </div>

                    </Card>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Controls */}
            {N > visibleCards && (
              <>
                <button
                  onClick={handlePrev}
                  className="absolute top-1/2 -left-2 sm:-left-6 transform -translate-y-1/2 p-3 bg-[#1a1d21]/80 hover:bg-[#1a1d21] text-gray-400 hover:text-white rounded-full border border-gray-800 transition-all z-10 hover:scale-105"
                  title="Anterior"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={handleNext}
                  className="absolute top-1/2 -right-2 sm:-right-6 transform -translate-y-1/2 p-3 bg-[#1a1d21]/80 hover:bg-[#1a1d21] text-gray-400 hover:text-white rounded-full border border-gray-800 transition-all z-10 hover:scale-105"
                  title="Próximo"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}

            {/* Carousel indicators */}
            {N > 0 && (
              <div className="flex items-center justify-center space-x-1.5 mt-8">
                {mentors.map((_, i) => {
                  const isActive = (carouselIndex % mentors.length) === i;
                  return (
                    <div 
                      key={i}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        isActive ? 'w-6 bg-[#0FFF55]' : 'w-1.5 bg-gray-700'
                      }`}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Detailed Mentor Modal (Pop-up) */}
        {selectedMentor && (
          <div 
            className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-10 bg-black/85 backdrop-blur-sm overflow-y-auto"
            onClick={() => setSelectedMentor(null)}
          >
            <div 
              className="relative w-full max-w-4xl bg-[#141517] border border-white/5 rounded-3xl overflow-hidden shadow-2xl my-auto cursor-default"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Horizontal Banner */}
              <div className="relative h-48 sm:h-64 bg-gradient-to-r from-gray-900 to-gray-800">
                {selectedMentor.banner_url ? (
                  <img 
                    src={selectedMentor.banner_url} 
                    alt="Banner" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-r from-[#0FFF55]/5 to-[#0dd149]/5" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#141517] to-transparent" />
                
                {/* Close button */}
                <button
                  onClick={() => setSelectedMentor(null)}
                  className="absolute top-4 right-4 z-10 p-2 bg-black/60 hover:bg-black/90 text-white rounded-full transition-colors border border-white/10"
                >
                  <X size={20} />
                </button>

                {/* Overlapping Avatar */}
                <div className="absolute -bottom-12 left-8 w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 border-[#141517] bg-[#121316] overflow-hidden shadow-2xl flex items-center justify-center">
                  {selectedMentor.avatar_url ? (
                    <img 
                      src={selectedMentor.avatar_url} 
                      alt={selectedMentor.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-10 h-10 text-gray-500" />
                  )}
                </div>
              </div>

              {/* Modal Body */}
              <div className="pt-16 px-6 sm:px-8 pb-8 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-white">{selectedMentor.name}</h2>
                    <p className="text-sm text-[#0FFF55] font-semibold uppercase tracking-wider mt-1">{selectedMentor.title}</p>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <div className="flex items-center space-x-1.5 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full text-xs text-gray-200">
                      <Briefcase size={14} className="text-[#0FFF55]" />
                      <span>{selectedMentor.experience_years} anos exp.</span>
                    </div>
                    {!!selectedMentor.hourly_rate && parseFloat(selectedMentor.hourly_rate.toString()) > 0 && (
                      <div className="flex items-center space-x-1.5 bg-[#0FFF55]/10 border border-[#0FFF55]/20 px-3 py-1.5 rounded-full text-xs font-semibold text-[#0FFF55]">
                        <DollarSign size={14} />
                        <span>{formatCurrency(parseFloat(selectedMentor.hourly_rate.toString()))}/h</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bio */}
                {selectedMentor.bio && (
                  <div className="space-y-2">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Sobre</h3>
                    <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{selectedMentor.bio}</p>
                  </div>
                )}

                {/* Specialties & Session Types */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-white/5 pt-6">
                  {selectedMentor.specialties && selectedMentor.specialties.filter(Boolean).length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Especialidades</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedMentor.specialties.filter(Boolean).map((spec, i) => (
                          <Badge key={i} className="bg-white/5 text-gray-300 border-white/10 text-xs px-2.5 py-1">
                            {spec}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {selectedMentor.session_types && selectedMentor.session_types.filter(Boolean).length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Tipos de Sessão</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedMentor.session_types.filter(Boolean).map((sess, i) => (
                          <Badge key={i} className="bg-[#1a1d21] text-[#0FFF55] border-gray-800 text-xs px-2.5 py-1">
                            {sess}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Contact and Checkout buttons */}
                <div className="border-t border-white/5 pt-6 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {selectedMentor.contact_email && selectedMentor.contact_email.trim() && (
                      <div className="relative">
                        {copiedEmailMentorId === selectedMentor.id && (
                          <div className="absolute bottom-full left-0 mb-2 px-3 py-1.5 bg-[#141517] border border-[#0FFF55]/30 text-white text-[11px] rounded-xl z-20 whitespace-nowrap">
                            Copiado!
                          </div>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-white/10 text-gray-300 hover:text-white text-xs"
                          onClick={() => {
                            navigator.clipboard.writeText(selectedMentor.contact_email!);
                            setCopiedEmailMentorId(selectedMentor.id);
                            setTimeout(() => setCopiedEmailMentorId(null), 2000);
                          }}
                        >
                          <Mail className="w-3.5 h-3.5 mr-1.5" />
                          E-mail
                        </Button>
                      </div>
                    )}
                    {(() => {
                      const igs = getMentorSocials(selectedMentor, 'instagram');
                      return igs.map((entry, idx) => (
                        <Button 
                          key={`detail-ig-${idx}`}
                          size="sm" 
                          variant="outline"
                          className="border-white/10 text-gray-300 hover:text-[#E4405F] hover:border-[#E4405F]/40 text-xs"
                          onClick={() => window.open(entry.url, '_blank')}
                          title={entry.url}
                        >
                          <Instagram className="w-3.5 h-3.5 mr-1.5 text-[#E4405F]" />
                          {entry.label || (igs.length > 1 ? `Instagram ${idx + 1}` : 'Instagram')}
                        </Button>
                      ));
                    })()}
                    {selectedMentor.linkedin_url && selectedMentor.linkedin_url.trim() && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-white/10 text-gray-300 hover:text-white text-xs"
                        onClick={() => window.open(selectedMentor.linkedin_url, '_blank')}
                      >
                        <Linkedin className="w-3.5 h-3.5 mr-1.5" />
                        LinkedIn
                      </Button>
                    )}
                    {selectedMentor.github_url && selectedMentor.github_url.trim() && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-white/10 text-gray-300 hover:text-white text-xs"
                        onClick={() => window.open(selectedMentor.github_url, '_blank')}
                      >
                        <Github className="w-3.5 h-3.5 mr-1.5" />
                        GitHub
                      </Button>
                    )}
                    {selectedMentor.whatsapp && selectedMentor.whatsapp.trim() && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-green-500/20 text-green-400 hover:text-green-300 hover:bg-green-500/10 text-xs"
                        onClick={() => window.open(selectedMentor.whatsapp!.startsWith('http') ? selectedMentor.whatsapp! : `https://wa.me/${selectedMentor.whatsapp!.replace(/\D/g, '')}`, '_blank')}
                      >
                        <MessageCircle className="w-3.5 h-3.5 mr-1.5" />
                        WhatsApp
                      </Button>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-2">
                    <Button 
                      size="md" 
                      variant="outline"
                      className="border-[#0FFF55]/40 text-[#0FFF55] hover:bg-[#0FFF55]/10 font-bold px-4 py-2.5 rounded-xl uppercase tracking-wider text-xs"
                      onClick={() => {
                        const s = selectedMentor.slug || slugify(selectedMentor.name);
                        window.open(`/bio-${s}`, '_blank');
                      }}
                    >
                      <Share2 className="w-3.5 h-3.5 mr-1.5" />
                      Ver Link da Bio
                    </Button>

                    {selectedMentor.checkout_url && (
                      <Button 
                        size="md" 
                        className="bg-[#0FFF55] hover:bg-[#0dd149] text-black font-bold px-6 py-2.5 rounded-xl uppercase tracking-wider text-xs shadow-lg shadow-[#0FFF55]/10 hover:shadow-[#0FFF55]/20"
                        onClick={() => window.open(selectedMentor.checkout_url, '_blank')}
                      >
                        Ir para a Mentoria
                      </Button>
                    )}
                  </div>
                </div>

                {/* Admin Controls */}
                {canEditMentor(selectedMentor) && (
                  <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-white/5">
                    <Button
                      onClick={() => {
                        const target = selectedMentor;
                        setSelectedMentor(null);
                        handleOpenConfigureBio(target, 'bio');
                      }}
                      size="sm"
                      className="bg-[#0FFF55]/15 hover:bg-[#0FFF55]/25 text-[#0FFF55] border border-[#0FFF55]/30 text-xs font-bold"
                    >
                      <Settings className="w-3 h-3 mr-1" />
                      Configurar Bio Links & Redes
                    </Button>

                    <Button
                      onClick={() => {
                        handleEdit(selectedMentor);
                        setSelectedMentor(null);
                      }}
                      size="sm"
                      variant="ghost"
                      className="text-gray-400 hover:text-white text-xs"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Editar Dados
                    </Button>

                    {isAdmin && (
                      <Button
                        onClick={() => {
                          handleDelete(selectedMentor.id);
                          setSelectedMentor(null);
                        }}
                        size="sm"
                        variant="ghost"
                        className="text-gray-400 hover:text-red-400 text-xs"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Excluir
                      </Button>
                    )}
                  </div>
                )}

                {/* Free Content Section */}
                <div className="border-t border-white/5 pt-6 space-y-4">
                  {editingFreeContentMentorId === selectedMentor.id ? (
                    /* Edit Free Content form */
                    <div className="space-y-4 bg-white/5 border border-white/10 p-5 rounded-2xl">
                      <div className="flex items-center justify-between border-b border-gray-800 pb-3">
                        <h3 className="text-sm font-bold text-white">Gerenciar Conteúdos Gratuitos</h3>
                        <div className="flex space-x-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="border-gray-700 text-gray-300 text-xs"
                            onClick={() => setEditingFreeContentMentorId(null)}
                          >
                            Cancelar
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            className="bg-[#0FFF55] hover:bg-[#0dd149] text-black font-bold text-xs"
                            onClick={async () => {
                              await handleSaveFreeContent(selectedMentor.id);
                              // Refresh selected mentor data
                              const updatedMentor = mentors.find(m => m.id === selectedMentor.id);
                              if (updatedMentor) {
                                setSelectedMentor({
                                  ...updatedMentor,
                                  free_content: editFreeContentForm
                                });
                              }
                            }}
                            disabled={savingFreeContent}
                          >
                            {savingFreeContent ? 'Salvando...' : 'Salvar'}
                          </Button>
                        </div>
                      </div>
                      {/* Form fields */}
                      <div className="space-y-3">
                        <div>
                          <Label className="text-gray-400 text-xs">Título da Seção</Label>
                          <Input
                            value={editFreeContentForm.title}
                            onChange={(e) => setEditFreeContentForm(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="Ex: Conteúdos Gratuitos"
                            className="bg-gray-800/40 border-gray-700 text-white mt-1 text-xs"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-xs">Vídeos Anexos</Label>
                          {editFreeContentForm.videos.map((video, idx) => (
                            <div key={idx} className="flex gap-2 items-center bg-gray-900/30 p-2.5 rounded-lg border border-gray-800/50">
                              <div className="flex-1 space-y-1">
                                <Input
                                  value={video.title}
                                  onChange={(e) => {
                                    const updated = [...editFreeContentForm.videos];
                                    updated[idx].title = e.target.value;
                                    setEditFreeContentForm(prev => ({ ...prev, videos: updated }));
                                  }}
                                  placeholder="Título do Vídeo"
                                  className="bg-gray-800/20 border-gray-700/60 text-white text-xs h-8"
                                />
                                <Input
                                  value={video.url}
                                  onChange={(e) => {
                                    const updated = [...editFreeContentForm.videos];
                                    updated[idx].url = e.target.value;
                                    setEditFreeContentForm(prev => ({ ...prev, videos: updated }));
                                  }}
                                  placeholder="URL do Vídeo (YouTube)"
                                  className="bg-gray-800/20 border-gray-700/60 text-white text-xs h-8"
                                />
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="text-red-400 hover:text-red-300 p-1"
                                onClick={() => {
                                  const updated = editFreeContentForm.videos.filter((_, i) => i !== idx);
                                  setEditFreeContentForm(prev => ({ ...prev, videos: updated }));
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="w-full border-dashed border-gray-700 hover:border-[#0FFF55]/40 text-gray-400 hover:text-white text-xs"
                            onClick={() => {
                              setEditFreeContentForm(prev => ({
                                ...prev,
                                videos: [...prev.videos, { title: '', url: '' }]
                              }));
                            }}
                          >
                            <PlusCircle className="w-4 h-4 mr-1" /> Adicionar Vídeo
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Display Free Content */
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                          {selectedMentor.free_content?.title || 'Conteúdos Gratuitos'}
                        </h3>
                        {canEditMentor(selectedMentor) && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="text-gray-400 hover:text-[#0FFF55] text-xs"
                            onClick={() => handleEditFreeContent(selectedMentor)}
                          >
                            <Edit className="w-3.5 h-3.5 mr-1" />
                            Gerenciar Conteúdos
                          </Button>
                        )}
                      </div>
                      
                      {selectedMentor.free_content?.videos && selectedMentor.free_content.videos.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                          {selectedMentor.free_content.videos.map((video, idx) => {
                            const youtubeId = extractYouTubeVideoId(video.url);
                            return (
                              <div
                                key={idx}
                                className="flex flex-col gap-3 bg-[#18181b]/40 border border-neutral-800/60 rounded-2xl p-3 hover:border-[#0FFF55]/30 hover:bg-neutral-950/70 transition-all duration-200 cursor-pointer group"
                                onClick={() => video.url && setPlayingVideo(video)}
                              >
                                {/* Thumbnail */}
                                <div className="w-full aspect-video rounded-xl bg-neutral-950 overflow-hidden relative border border-neutral-800/80">
                                  {youtubeId ? (
                                    <img
                                      src={buildYouTubeThumbnailUrl(youtubeId)}
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                      alt={video.title}
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-neutral-900">
                                      <Video className="w-6 h-6 text-gray-500" />
                                    </div>
                                  )}
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/45 group-hover:bg-black/20 transition-colors">
                                    <Play className="w-5 h-5 text-white fill-white" />
                                  </div>
                                </div>
                                {/* Video Title */}
                                <div className="px-1">
                                  <h4 className="text-white text-xs font-bold line-clamp-2 leading-snug group-hover:text-[#0FFF55] transition-colors" title={video.title}>
                                    {video.title || 'Vídeo sem título'}
                                  </h4>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">Nenhum conteúdo gratuito anexado a este mentor ainda.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Header Config Modal */}
      {isHeaderModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-md bg-[#1a1d23] border border-gray-800 rounded-2xl p-6 space-y-4">
            <h3 className="text-xl font-bold text-white">Editar Topo da Página</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-400">Título - Parte 1 (Texto Normal)</label>
                <input
                  type="text"
                  value={headerForm.titlePart1}
                  onChange={(e) => setHeaderForm({ ...headerForm, titlePart1: e.target.value })}
                  placeholder="Ex: Conheça os"
                  className="w-full mt-1 px-3 py-2 bg-[#0f1114] border border-gray-800 focus:border-[#0FFF55]/60 rounded-xl text-white text-sm outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#0FFF55]">Título - Destaque (Texto em Verde)</label>
                <input
                  type="text"
                  value={headerForm.titleHighlight}
                  onChange={(e) => setHeaderForm({ ...headerForm, titleHighlight: e.target.value })}
                  placeholder="Ex: mentores"
                  className="w-full mt-1 px-3 py-2 bg-[#0f1114] border border-gray-800 focus:border-[#0FFF55]/60 rounded-xl text-white text-sm outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-400">Título - Parte 2 (Texto Normal)</label>
                <input
                  type="text"
                  value={headerForm.titlePart2}
                  onChange={(e) => setHeaderForm({ ...headerForm, titlePart2: e.target.value })}
                  placeholder="Ex: da nossa comunidade"
                  className="w-full mt-1 px-3 py-2 bg-[#0f1114] border border-gray-800 focus:border-[#0FFF55]/60 rounded-xl text-white text-sm outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-400">Descrição</label>
                <textarea
                  value={headerForm.description}
                  onChange={(e) => setHeaderForm({ ...headerForm, description: e.target.value })}
                  rows={3}
                  className="w-full mt-1 px-3 py-2 bg-[#0f1114] border border-gray-800 focus:border-[#0FFF55]/60 rounded-xl text-white text-sm outline-none resize-none"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setIsHeaderModalOpen(false)}
                className="px-4 py-2 bg-transparent text-gray-400 hover:text-white text-xs font-semibold rounded-xl"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setHeaderConfig(headerForm);
                  localStorage.setItem('masterclub_mentors_header', JSON.stringify(headerForm));
                  setIsHeaderModalOpen(false);
                }}
                className="px-5 py-2 bg-[#0FFF55] hover:bg-[#0dd149] text-black text-xs font-bold rounded-xl"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {playingVideo && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md cursor-pointer"
          onClick={() => setPlayingVideo(null)}
        >
          <div 
            className="relative w-full max-w-4xl bg-[#141517] border border-white/10 rounded-2xl overflow-hidden shadow-2xl cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-[#141517]">
              <h3 className="text-white text-xs sm:text-sm font-bold line-clamp-2 leading-tight max-w-[85%] pr-2">
                {playingVideo.title}
              </h3>
              <button
                type="button"
                onClick={() => setPlayingVideo(null)}
                className="p-1 text-gray-400 hover:text-white rounded-lg hover:bg-gray-850 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Video Container */}
            <div className="relative aspect-video w-full bg-black">
              {(() => {
                const youtubeId = extractYouTubeVideoId(playingVideo.url);
                return youtubeId ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
                    title={playingVideo.title}
                    className="absolute inset-0 w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400 text-xs sm:text-sm p-4 text-center">
                    Não foi possível carregar este vídeo em iframe. 
                    <a href={playingVideo.url} target="_blank" rel="noopener noreferrer" className="text-[#0FFF55] underline ml-1 font-semibold">
                      Clique aqui para assistir diretamente no YouTube
                    </a>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Gestão de Slugs e Bio Links dos Mentores */}
      <MentorBioManagerModal
        isOpen={isBioManagerOpen}
        onClose={() => setIsBioManagerOpen(false)}
        mentors={mentors}
        onRefreshMentors={loadMentors}
        onOpenConfigureBio={(mentor) => handleOpenConfigureBio(mentor, 'bio')}
        onEditMentorCard={(mentor) => {
          setIsBioManagerOpen(false);
          handleEdit(mentor);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      {/* Modal do Editor Administrativo do Mentor (Configurador da Bio & Plataforma) */}
      {adminModalConfig && (
        <MentorAdminModal
          isOpen={isMentorAdminModalOpen}
          onClose={() => {
            setIsMentorAdminModalOpen(false);
            setActiveBioMentorTarget(null);
          }}
          config={adminModalConfig}
          onSaveConfig={handleSaveAdminConfig}
          initialTab={adminModalInitialTab}
        />
      )}
    </div>
  );
};

export default MentoriasPage; 